import React, { useEffect, useState } from "react";
import { fetchQuery } from "../../../utils/commonComponent/fetched";
import { AddButton, SearchToggleButton } from "../../../utils/Custom/Button";
import { AutoCompletes, DateInput } from "../../../utils/Custom/custominput";
import { getFormattedDates } from "../../../utils/utils";
import moment from "moment";
import { IconButton, Tooltip } from "@mui/material";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import ViewFileModalInspectionList from "../inspectionTaskList/viewFileModalInspectionList"; // adjust path
import { generateTimeSheetPDF } from "./timeSheetPDF";
import { getPermissions } from "../rbac/hasAccess";

const TimeSheetReport = (props) => {
  const permissions = getPermissions(props.moduleId);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [userList, setUserList] = useState([]);
  const [timeSheetList, setTimeSheetList] = useState([]);
  const [searched, setSearched] = React.useState(false);
  const [from, setFromDate] = useState(null);
  const [to, setToDate] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const [modalData, setModalData] = useState(null);

  const getQuery = () => {
    let whereConditions = [];

    // Date filter
    if (to && from) {
      const { fromDate, toDate } = getFormattedDates(from, to);
      whereConditions.push(`DATE(visitDate) BETWEEN '${fromDate}' AND '${toDate}'`);
    }

    // Inspector filter (IMPORTANT FIX)
    if (selectedUsers) {
      whereConditions.push(`createBy = ${selectedUsers}`);
    }

    // Only active records
    whereConditions.push(`status = 1`);

    const whereClause = whereConditions.length
      ? `WHERE ${whereConditions.join(" AND ")}`
      : "";

    const query = `SELECT * FROM timeSheet ts ${whereClause} ORDER BY visitDate DESC`;

    return query.trim();
  };

  const fetchUserList = async () => {
    try {
      const query = `SELECT id, userName FROM users WHERE status = 1 and type = 2`;
      const res = await fetchQuery(query);
      setUserList(res);
    } catch (err) {
      console.error("Error fetching users:", err);
    }
  };

  const fetchTimeSheetReport = async () => {
    try {
      const query = getQuery();
      const timeSheetReports = await fetchQuery(query);
      const formatted = timeSheetReports.map((item) => ({
        ...item,
        visitDate: item.visitDate
          ? moment(item.visitDate).format("DD-MM-YYYY")
          : "",
      }));
      setTimeSheetList(formatted);
    } catch (error) {
      console.error("Error fetching timeSheetReports data:", error);
    }
  };

  const handleSearch = () => {
    if (selectedUsers.length === 0) {
      alert("Please select an employee.");
      return;
    }
    fetchTimeSheetReport();
    setSearched(true);
  };

  const clearSearch = () => {
    setSearched(false);
    setFromDate(null);
    setToDate(null);
    setTimeSheetList([]);
    setSelectedUsers([]);
  };

  const handleOpenAttachments = (row) => {
    setModalData({ supportingDocuments: row.supportingDocuments });
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setModalData(null);
  };

  useEffect(() => {
    fetchUserList();
  }, []);

  return (
    <>
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-5 gap-4 my-3">
        <div className="w-full">
          <AutoCompletes
            label="Choose Inspector"
            options={userList}
            value={selectedUsers}
            onChange={(val) => {
              setSelectedUsers(val);
              setSearched(false);
            }}
            isWidth
            valueKey="id"
            labelKey="userName"
          />
        </div>
        <div className="w-full">
          <DateInput
            id="from-date"
            label="From Date"
            value={from}
            onChange={(val) => {
              setFromDate(val);
              setSearched(false);
            }}
          />
        </div>
        <div className="w-full">
          <DateInput
            id="to-date"
            label="To Date"
            value={to}
            onChange={(val) => {
              setToDate(val);
              setSearched(false);
            }}
          />
        </div>
        <div>
          <SearchToggleButton
            searched={searched}
            onSearch={handleSearch}
            onClear={clearSearch}
          />
        </div>
        {timeSheetList.length > 0 && (permissions.pdfDownload || permissions.allAccess) && (
          <div>
            <AddButton
              onClick={() => generateTimeSheetPDF(timeSheetList, userList.find(u => u.id === selectedUsers)?.userName || "")}
              label="Download PDF"
            />
          </div>
        )}
      </div>

      {/* Static Table */}
      <div className="w-full border rounded-md shadow">
        <div className="overflow-y-auto max-h-[450px]">
          <table className="min-w-full text-sm text-left text-gray-700">
            <thead className="sticky top-0 bg-blue-50 text-xs uppercase font-medium text-gray-600 z-10">
              <tr>
                <th className="px-4 py-3">Sr. No.</th>
                <th className="px-4 py-3">Client Project</th>
                <th className="px-4 py-3">Visit Date</th>
                <th className="px-4 py-3 text-center">Attachments</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {timeSheetList.length > 0 ? (
                timeSheetList.map((row, index) => (
                  <tr key={index}>
                    <td className="px-4 py-3">{index + 1}</td>
                    <td className="px-4 py-3">
                      <div className="block w-[250px] overflow-hidden whitespace-nowrap text-ellipsis" title={row.clientProject}>
                        {row.clientProject}
                      </div></td>
                    <td className="px-4 py-3">{row.visitDate}</td>
                    <td className="px-4 py-3 text-center">
                      <Tooltip title="View Attachments">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleOpenAttachments(row)}
                        >
                          <AttachFileIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={4} className="text-center py-4 text-gray-500">
                    No data available.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Attachments Modal */}
      <ViewFileModalInspectionList
        openModal={openModal}
        modalData={modalData}
        handleCloseModal={handleCloseModal}
        modalType="attachments"
      />
    </>
  );
};

export default TimeSheetReport;